import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, switchMap, tap, timer } from 'rxjs';
import { CommunicationChartPanelService } from '@app/pages/constructor/constructor-components/communication-chart/services/communication-chart-panel.service';
import { MessageNotificationViewModel } from '@app/pages/constructor/constructor-components/communication-chart/models/message-notification-view.model';
import { MessageNotificationProgress } from '@app/pages/constructor/constructor-components/communication-chart/enum/message-notification-progress.enum';
import { map, withLatestFrom } from 'rxjs/operators';
import { CommercialDocumentProcessType } from '@app/pages/commercial-documents/components/commercial-document-list/enums/commercial-document-list.enum';
import { ChatRoomType } from '@app/pages/constructor/constructor-components/communication-chart/enum/chat-room-type.enum';
import { CommunicationChartGetParams } from '@app/pages/constructor/constructor-components/communication-chart/models/communication-chart-panel.model';
import { CommunicationChartUnreadCountModel } from '@app/pages/constructor/constructor-components/communication-chart/models/communication-chart-unread-count.model';

interface State {
  commentsChartsAlert: MessageNotificationViewModel[];
  commentsChartsPanel: MessageNotificationViewModel[];
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  countUnread: CommunicationChartUnreadCountModel;
}

@Injectable()
export class CommunicationChartPanelStore extends ComponentStore<State> {
  private messageNotificationProgress = MessageNotificationProgress;

  private readonly commercialDocumentProcessType =
    CommercialDocumentProcessType;
  private readonly chatRoomType = ChatRoomType;

  constructor(private communicationChartPanel: CommunicationChartPanelService) {
    super({
      commentsChartsAlert: [],
      commentsChartsPanel: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
      countUnread: {
        totalMessageNotificationsCount: 0,
        newMessageNotificationsCount: 0,
        openedMessageNotificationsCount: 0
      }
    });
  }

  public readonly getCommunicationChartPanel =
    this.effect<CommunicationChartGetParams>(trigger$ =>
      trigger$.pipe(
        withLatestFrom(
          this.select(state => state.currentPage),
          this.select(state => state.pageSize)
        ),
        switchMap(([data, currentPage, pageSize]) => {
          const pageToLoad = data.currentPage ?? currentPage;
          return this.communicationChartPanel
            .getCommunicationChartPanel(
              pageToLoad,
              pageSize,
              data.progressMessages,
              data.isGetAll
            )
            .pipe(
              tap(response => {
                this.patchState(() => ({
                  totalItems: response.totalItems,
                  totalPages: Math.ceil(response.totalItems / pageSize)
                }));
                this.getCountUnreadCommunicationChart();
              }),
              map(messages =>
                messages.items.map(msg => ({
                  ...msg,
                  link: this.buildLink(msg)
                }))
              ),
              tap(messages => {
                this.patchState(state => ({
                  commentsChartsPanel:
                    pageToLoad === 1
                      ? messages
                      : [...state.commentsChartsPanel, ...messages],
                  currentPage: pageToLoad + 1
                }));
              }),
              catchError(() => EMPTY)
            );
        })
      )
    );

  public readonly loadNextPage = this.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(
        this.select(state => state.currentPage),
        this.select(state => state.pageSize),
        this.select(state => state.totalPages)
      ),
      switchMap(([_, page, pageSize, totalPages]) => {
        if (page > totalPages) return EMPTY;

        return this.communicationChartPanel
          .getCommunicationChartPanel(page, pageSize, null, false)
          .pipe(
            tap(response => {
              this.patchState(() => ({
                totalItems: response.totalItems,
                totalPages: Math.ceil(response.totalItems / pageSize)
              }));
            }),
            map(response =>
              response.items.map(msg => ({
                ...msg,
                link: this.buildLink(msg)
              }))
            ),
            tap(newItems => {
              this.patchState(state => ({
                commentsChartsPanel: [
                  ...state.commentsChartsPanel,
                  ...newItems
                ],
                currentPage: state.currentPage + 1
              }));
            }),
            catchError(() => EMPTY)
          );
      })
    )
  );

  private buildLink(msg: MessageNotificationViewModel): string | undefined {
    switch (msg.roomType) {
      case this.chatRoomType.PurchaseRequest:
        return `/procurement/pr/pr-list/${msg.entityId}`;
      case this.chatRoomType.Rfx:
        return `/procurement/tender-auction/tender-journal/${msg.entityId}`;
      case this.chatRoomType.CommercialDocument:
        switch (msg.commercialDocumentProcessType) {
          case this.commercialDocumentProcessType.Order:
            return `/commercial-documents/orders/order-list/${msg.entityId}`;
          case this.commercialDocumentProcessType.Agreement:
            return `/commercial-documents/agreements/agreement-list/${msg.entityId}`;
          case this.commercialDocumentProcessType.Receipt:
            return undefined;
          default:
            return undefined;
        }
      default:
        return undefined;
    }
  }

  public readonly setCommunicationChartAlert =
    this.effect<MessageNotificationViewModel>(trigger$ =>
      trigger$.pipe(
        map(msg => ({
          ...msg,
          link: this.buildLink(msg)
        })),
        tap(notificationsAlert => {
          this.patchState(state => ({
            commentsChartsAlert: [
              notificationsAlert,
              ...state.commentsChartsAlert
            ]
          }));
          timer(2000).subscribe(() => {
            this.getCommunicationChartPanel({
              isGetAll: true,
              progressMessages: this.messageNotificationProgress.Unread
            });
          });
        }),
        catchError(() => EMPTY)
      )
    );

  public readonly deleteCommunicationNotification = this.effect<string>(
    trigger$ =>
      trigger$.pipe(
        switchMap(notifyId => {
          return this.communicationChartPanel.deleteCommunicationChartNotification(
            notifyId
          );
        }),
        catchError(() => EMPTY)
      )
  );

  public readonly viewCommunicationNotify = this.effect<string>(trigger$ =>
    trigger$.pipe(
      switchMap(notifyId => {
        return this.communicationChartPanel.viewCommunicationNotifyById(
          notifyId
        );
      }),
      catchError(() => EMPTY)
    )
  );

  public readonly viewAllCommunicationNotify = this.effect<void>(trigger$ =>
    trigger$.pipe(
      switchMap(() => {
        return this.communicationChartPanel.viewAllCommunicationChartNotify();
      }),
      catchError(() => EMPTY)
    )
  );

  public readonly getCountUnreadCommunicationChart = this.effect<void>(
    trigger$ =>
      trigger$.pipe(
        switchMap(() => {
          return this.communicationChartPanel
            .getCountUnreadNotificationChart()
            .pipe(
              tap(data => {
                this.patchState({
                  countUnread: data
                });
              })
            );
        }),
        catchError(() => EMPTY)
      )
  );

  public readonly commentsUnreadCount$ = this.select(
    state => state.countUnread
  );

  public readonly commentsChartsAlert$ = this.select(
    state => state.commentsChartsAlert
  );

  public readonly commentsChartsPanel$ = this.select(
    state => state.commentsChartsPanel
  );
}
